<script setup lang="ts">
export type StatusType = 'live' | 'upcoming' | 'completed' | 'win' | 'loss' | 'draw' | 'no_contest'

type IconType = 'check' | 'x' | 'dash' | 'circle-slash' | 'clock' | 'broadcast' | 'check-circle' | null

interface Props {
  status: StatusType
  showLabel?: boolean
  size?: 'sm' | 'md'
}

const props = withDefaults(defineProps<Props>(), {
  showLabel: true,
  size: 'md'
})

// Result types always show abbreviated text labels for colorblind accessibility
const isResultType = (status: StatusType): boolean => {
  return ['win', 'loss', 'draw', 'no_contest'].includes(status)
}

// Event status types must always show text labels for WCAG 1.4.1 compliance
// (information must not be conveyed by color alone)
const isEventStatusType = (status: StatusType): boolean => {
  return ['live', 'upcoming', 'completed'].includes(status)
}

const getStatusConfig = (status: StatusType): { classes: string; label: string; shortLabel: string; icon: IconType } => {
  switch (status) {
    case 'live':
      return {
        classes: 'bg-ufc-red animate-pulse',
        label: 'Live',
        shortLabel: 'Live',
        icon: 'broadcast'
      }
    case 'upcoming':
      return {
        classes: 'bg-ufc-gold text-black',
        label: 'Upcoming',
        shortLabel: 'Upcoming',
        icon: 'clock'
      }
    case 'completed':
      return {
        classes: 'bg-gray-600',
        label: 'Completed',
        shortLabel: 'Completed',
        icon: 'check-circle'
      }
    case 'win':
      return {
        classes: 'bg-green-700 text-white',
        label: 'Win',
        shortLabel: 'W',
        icon: 'check'
      }
    case 'loss':
      return {
        classes: 'bg-red-600 text-white',
        label: 'Loss',
        shortLabel: 'L',
        icon: 'x'
      }
    case 'draw':
      return {
        classes: 'bg-gray-600 text-white',
        label: 'Draw',
        shortLabel: 'D',
        icon: 'dash'
      }
    case 'no_contest':
      return {
        classes: 'bg-gray-600 text-white',
        label: 'No Contest',
        shortLabel: 'NC',
        icon: 'circle-slash'
      }
    default:
      return {
        classes: 'bg-gray-600',
        label: status,
        shortLabel: status,
        icon: null
      }
  }
}

const config = getStatusConfig(props.status)

const getSizeClasses = () => {
  return props.size === 'sm'
    ? 'px-1.5 py-0.5 text-xs'
    : 'px-3 py-1 text-xs'
}

const getIconSize = () => {
  return props.size === 'sm' ? 10 : 12
}
</script>

<template>
  <span
    :class="[
      config.classes,
      getSizeClasses(),
      'inline-flex items-center gap-1 font-bold uppercase rounded'
    ]"
    role="status"
    :aria-label="config.label"
  >
    <!-- Clock icon for upcoming -->
    <svg
      v-if="config.icon === 'clock'"
      aria-hidden="true"
      :width="getIconSize()"
      :height="getIconSize()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>

    <!-- Broadcast icon for live -->
    <svg
      v-if="config.icon === 'broadcast'"
      aria-hidden="true"
      :width="getIconSize()"
      :height="getIconSize()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
      <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
      <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
    </svg>

    <!-- Check-circle icon for completed -->
    <svg
      v-if="config.icon === 'check-circle'"
      aria-hidden="true"
      :width="getIconSize()"
      :height="getIconSize()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 11.5 14.5 16 9" />
    </svg>

    <!-- Checkmark icon for win -->
    <svg
      v-if="config.icon === 'check'"
      aria-hidden="true"
      :width="getIconSize()"
      :height="getIconSize()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>

    <!-- X icon for loss -->
    <svg
      v-if="config.icon === 'x'"
      aria-hidden="true"
      :width="getIconSize()"
      :height="getIconSize()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>

    <!-- Dash icon for draw -->
    <svg
      v-if="config.icon === 'dash'"
      aria-hidden="true"
      :width="getIconSize()"
      :height="getIconSize()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>

    <!-- Circle with slash icon for no contest -->
    <svg
      v-if="config.icon === 'circle-slash'"
      aria-hidden="true"
      :width="getIconSize()"
      :height="getIconSize()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>

    <!-- Result types always show abbreviated text label for colorblind accessibility -->
    <span v-if="isResultType(status)">{{ config.shortLabel }}</span>
    <!-- Event status types always show text labels for WCAG 1.4.1 compliance -->
    <span v-else-if="isEventStatusType(status)">{{ config.label }}</span>
    <!-- Other types respect showLabel prop -->
    <span v-else-if="showLabel">{{ config.label }}</span>
  </span>
</template>
